/**
 * Utility function to validate E-way bill address requirements
 * @param {Object} partyBilling - Billing address data
 * @param {Object} partyDelivery - Delivery address data
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validateEwaybillAddresses = (partyBilling, partyDelivery, type) => {
  // Define required fields for address validation
  const requiredFields = [
    { key: 'line1', label: 'Address Details' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'countrycode', label: 'Country' },
    { key: 'statecode', label: 'State' },
    { key: 'district', label: 'District' },
    { key: 'city', label: 'City' },
  ];

  // Check individual fields for billing address
  const billingMissingFields = [];
  requiredFields.forEach((field) => {
    if (!partyBilling?.[field.key]) {
      billingMissingFields.push(field.label);
    }
  });

  // Check individual fields for delivery address
  const deliveryMissingFields = [];
  requiredFields.forEach((field) => {
    if (!partyDelivery?.[field.key]) {
      deliveryMissingFields.push(field.label);
    }
  });

  const hasBillingIssues = billingMissingFields.length > 0;
  const hasDeliveryIssues = deliveryMissingFields.length > 0;

  let isValid = true;
  let errorMessage = '';
  if (type && hasBillingIssues) {
    isValid = false;
    const billingFields = billingMissingFields.join(', ');
    errorMessage = `Please fill ${billingFields} of Billing address to proceed with E-invoice creation`;
  }
  if (!type && hasBillingIssues && hasDeliveryIssues) {
    isValid = false;
    const billingFields = billingMissingFields.join(', ');
    const deliveryFields = deliveryMissingFields.join(', ');
    errorMessage = `Please fill ${billingFields} of Billing address and ${deliveryFields} of Shipping address to proceed with E-Way Bill creation`;
  } else if (!type && hasBillingIssues && !hasDeliveryIssues) {
    isValid = false;
    const billingFields = billingMissingFields.join(', ');
    errorMessage = `Please fill ${billingFields} of Billing address to proceed with E-Way Bill creation`;
  } else if (!type && !hasBillingIssues && hasDeliveryIssues) {
    isValid = false;
    const deliveryFields = deliveryMissingFields.join(', ');
    errorMessage = `Please fill ${deliveryFields} of Shipping address to proceed with E-Way Bill creation`;
  }

  return {
    isValid,
    errorMessage,
    billingMissingFields,
    deliveryMissingFields,
    hasBillingIssues,
    hasDeliveryIssues,
  };
};

/**
 * Utility function to validate E-way bill addresses from GraphQL response
 * @param {Object} resPartyAddressEntityDetails - GraphQL response containing party address data
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validateEwaybillFromResponse = (resPartyAddressEntityDetails, type) => {
  const partyBilling = resPartyAddressEntityDetails?.data?.partyBilling?.[0];
  const partyDelivery = resPartyAddressEntityDetails?.data?.partyDelivery?.[0] || [];

  return validateEwaybillAddresses(partyBilling, partyDelivery, type);
};

/**
 * Hook-like function to handle E-way bill validation with callback
 * @param {Object} resPartyAddressEntityDetails - GraphQL response containing party address data
 * @param {Function} showWarningSnackbar - Function to show warning messages
 * @param {Function} setBackdropOpen - Function to control backdrop state
 * @returns {boolean} - Returns true if validation passed, false if validation failed
 */
export const handleEwaybillValidation = (resPartyAddressEntityDetails, showWarningSnackbar, setBackdropOpen, type) => {
  const validation = validateEwaybillFromResponse(resPartyAddressEntityDetails, type);

  if (!validation.isValid) {
    showWarningSnackbar(validation.errorMessage);
    setBackdropOpen(false);
    return false;
  }

  return true;
};
